import React from 'react';
import {
  Box,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Chip,
  Button,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  SmartToy as AIIcon,
  Assignment as StatusIcon,
} from '@mui/icons-material';

interface ApplicationFiltersProps {
  showAIGeneratedOnly: boolean;
  onToggleAIGenerated: (show: boolean) => void;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  onClearFilters: () => void;
  totalCount: number;
  filteredCount: number;
}

const ApplicationFilters: React.FC<ApplicationFiltersProps> = ({
  showAIGeneratedOnly,
  onToggleAIGenerated,
  statusFilter,
  onStatusFilterChange,
  onClearFilters,
  totalCount,
  filteredCount,
}) => {
  const hasActiveFilters = showAIGeneratedOnly || statusFilter !== 'all';

  return (
    <Card sx={{ p: 2, borderRadius: 2, boxShadow: 1, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterListIcon color="primary" />
          <Typography variant="subtitle1" fontWeight="600">
            Filters
          </Typography>
        </Box>

        <Divider orientation="vertical" flexItem />

        <FormControlLabel
          control={
            <Switch
              checked={showAIGeneratedOnly}
              onChange={(e) => onToggleAIGenerated(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AIIcon fontSize="small" color="primary" />
              <Typography variant="body2">
                AI Generated Only
              </Typography>
            </Box>
          }
        />

        <Divider orientation="vertical" flexItem />

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value)}
            label="Status"
            sx={{ height: 40 }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="submitted">Submitted</MenuItem>
            <MenuItem value="review">Under Review</MenuItem>
            <MenuItem value="accepted">Accepted</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
            <MenuItem value="withdrawn">Withdrawn</MenuItem>
          </Select>
        </FormControl>

        {hasActiveFilters && (
          <>
            <Divider orientation="vertical" flexItem />
            <Button
              startIcon={<ClearIcon />}
              onClick={onClearFilters}
              variant="outlined"
              size="small"
              sx={{ textTransform: 'none' }}
            >
              Clear Filters
            </Button>
          </>
        )}

        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredCount} of {totalCount} applications
          </Typography>
          {hasActiveFilters && (
            <Chip
              label={`${totalCount - filteredCount} hidden`}
              size="small"
              color="secondary"
              variant="outlined"
            />
          )}
        </Box>
      </Box>
    </Card>
  );
};

export default ApplicationFilters;
